import { toLocalDateStr } from "@/utils/date";
import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { API, AuthContext } from "@/App";
import { toast } from "sonner";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Users,
  Target,
  FileText,
  Clock,
  BarChart3,
  PieChart,
  Calendar
} from "lucide-react";

// Stat Card Component - moved outside
const StatCard = ({ title, value, subtitle, icon: Icon, color = "indigo" }) => {
  const colorClasses = {
    indigo: { text: "text-indigo-600", bg: "bg-indigo-100" },
    green: { text: "text-green-600", bg: "bg-green-100" },
    blue: { text: "text-blue-600", bg: "bg-blue-100" },
    purple: { text: "text-purple-600", bg: "bg-purple-100" },
  };
  const colors = colorClasses[color] || colorClasses.indigo;
  
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-500">{title}</p>
            <p className={`text-2xl font-bold ${colors.text}`}>{value}</p>
            {subtitle && <p className="text-xs text-slate-400 mt-1">{subtitle}</p>}
          </div>
          <div className={`p-3 rounded-full ${colors.bg}`}>
            <Icon className={`w-6 h-6 ${colors.text}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Funnel Bar Component - moved outside
const FunnelBar = ({ stage, count, percentage, maxCount }) => {
  const width = maxCount > 0 ? (count / maxCount * 100) : 0;
  return (
    <div className="mb-3">
      <div className="flex justify-between text-sm mb-1">
        <span className="font-medium">{stage}</span>
        <span className="text-slate-500">{count} ({percentage}%)</span>
      </div>
      <div className="h-6 bg-slate-100 rounded-full overflow-hidden">
        <div 
          className="h-full bg-gradient-to-r from-indigo-500 to-indigo-400 rounded-full transition-all duration-500"
          style={{ width: `${width}%` }}
        />
      </div>
    </div>
  );
};

export default function AnalyticsPage() {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    start_date: toLocalDateStr(new Date(new Date().setMonth(new Date().getMonth() - 3))),
    end_date: toLocalDateStr(new Date())
  });
  
  const [leadConversion, setLeadConversion] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [teamProductivity, setTeamProductivity] = useState(null);
  const [estimatePerformance, setEstimatePerformance] = useState(null);

  useEffect(() => {
    fetchAnalytics();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const params = `start_date=${dateRange.start_date}&end_date=${dateRange.end_date}`;
      
      const [conversionRes, revenueRes, productivityRes, estimateRes] = await Promise.all([
        axios.get(`${API}/analytics/lead-conversion?${params}`),
        axios.get(`${API}/analytics/revenue?${params}`),
        axios.get(`${API}/analytics/team-productivity?${params}`).catch(() => ({ data: null })),
        axios.get(`${API}/analytics/estimate-performance?${params}`)
      ]);
      
      setLeadConversion(conversionRes.data);
      setRevenue(revenueRes.data);
      setTeamProductivity(productivityRes.data);
      setEstimatePerformance(estimateRes.data);
    } catch (error) {
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="analytics-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Analytics Dashboard</h1>
          <p className="text-slate-600">Track your business performance</p>
        </div>
        
        {/* Date Range Filter */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Label className="text-sm">From</Label>
            <Input
              type="date"
              value={dateRange.start_date}
              onChange={(e) => setDateRange({ ...dateRange, start_date: e.target.value })}
              className="w-40"
            />
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-sm">To</Label>
            <Input
              type="date"
              value={dateRange.end_date}
              onChange={(e) => setDateRange({ ...dateRange, end_date: e.target.value })}
              className="w-40"
            />
          </div>
          <Button onClick={fetchAnalytics} variant="outline" size="sm">
            <Calendar className="w-4 h-4 mr-2" />
            Apply
          </Button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Revenue"
          value={`$${(revenue?.total_revenue || 0).toLocaleString()}`}
          subtitle={`${revenue?.total_estimates || 0} accepted estimates`}
          icon={DollarSign}
          color="green"
        />
        <StatCard
          title="Conversion Rate"
          value={`${leadConversion?.conversion_rate || 0}%`}
          subtitle={`${leadConversion?.won || 0} won / ${leadConversion?.total_leads || 0} total`}
          icon={Target}
          color="indigo"
        />
        <StatCard
          title="Estimate Win Rate"
          value={`${estimatePerformance?.win_rate || 0}%`}
          subtitle={`${estimatePerformance?.accepted?.count || 0} accepted`}
          icon={FileText}
          color="blue"
        />
        <StatCard
          title="Avg Estimate Value"
          value={`$${(revenue?.average_estimate_value || 0).toLocaleString()}`}
          subtitle="Per accepted estimate"
          icon={BarChart3}
          color="purple"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Lead Conversion Funnel */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="w-5 h-5" />
              Lead Conversion Funnel
            </CardTitle>
          </CardHeader>
          <CardContent>
            {leadConversion?.funnel?.length > 0 ? (
              <div>
                {leadConversion.funnel.map((stage, idx) => (
                  <FunnelBar
                    key={`analytics-${idx}-${stage.stage}`}
                    stage={stage.stage}
                    count={stage.count}
                    percentage={stage.percentage}
                    maxCount={leadConversion.total_leads}
                  />
                ))}
                <div className="mt-4 pt-4 border-t flex justify-between text-sm">
                  <span>
                    <span className="font-medium text-green-600">{leadConversion.won}</span> Won
                  </span>
                  <span>
                    <span className="font-medium text-red-600">{leadConversion.lost}</span> Lost
                  </span>
                  <span>
                    Win Rate: <span className="font-bold">{leadConversion.win_rate}%</span>
                  </span>
                </div>
              </div>
            ) : (
              <p className="text-slate-500 text-center py-8">No lead data available</p>
            )}
          </CardContent>
        </Card>

        {/* Revenue by Category */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PieChart className="w-5 h-5" />
              Revenue by Category
            </CardTitle>
          </CardHeader>
          <CardContent>
            {revenue?.data?.length > 0 ? (
              <div className="space-y-4">
                {revenue.data.map((item, idx) => {
                  const percentage = revenue.total_revenue > 0 
                    ? (item.total_revenue / revenue.total_revenue * 100).toFixed(1)
                    : 0;
                  const colors = ['bg-indigo-500', 'bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-amber-500'];
                  return (
                    <div key={idx}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="font-medium">{item.label}</span>
                        <span className="text-slate-500">
                          ${item.total_revenue.toLocaleString()} ({percentage}%)
                        </span>
                      </div>
                      <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                        <div 
                          className={`h-full ${colors[idx % colors.length]} rounded-full`}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                      <p className="text-xs text-slate-400 mt-1">
                        {item.count} estimates • Avg: ${item.avg_value.toLocaleString()}
                      </p>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-slate-500 text-center py-8">No revenue data available</p>
            )}
          </CardContent>
        </Card>

        {/* Estimate Performance */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Estimate Performance
            </CardTitle>
          </CardHeader>
          <CardContent>
            {estimatePerformance ? (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div className="p-4 bg-green-50 rounded-lg">
                    <p className="text-2xl font-bold text-green-600">
                      {estimatePerformance.accepted?.count || 0}
                    </p>
                    <p className="text-sm text-green-700">Accepted</p>
                    <p className="text-xs text-green-600">
                      ${(estimatePerformance.total_accepted_value || 0).toLocaleString()}
                    </p>
                  </div>
                  <div className="p-4 bg-amber-50 rounded-lg">
                    <p className="text-2xl font-bold text-amber-600">
                      {estimatePerformance.pending?.count || 0}
                    </p>
                    <p className="text-sm text-amber-700">Pending</p>
                    <p className="text-xs text-amber-600">
                      ${(estimatePerformance.pending?.total_value || 0).toLocaleString()}
                    </p>
                  </div>
                  <div className="p-4 bg-red-50 rounded-lg">
                    <p className="text-2xl font-bold text-red-600">
                      {estimatePerformance.rejected?.count || 0}
                    </p>
                    <p className="text-sm text-red-700">Rejected</p>
                    <p className="text-xs text-red-600">
                      ${(estimatePerformance.total_rejected_value || 0).toLocaleString()}
                    </p>
                  </div>
                </div>
                <div className="pt-4 border-t">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Win Rate</span>
                    <span className="text-xl font-bold text-indigo-600">
                      {estimatePerformance.win_rate}%
                    </span>
                  </div>
                  <div className="mt-2 h-3 bg-slate-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-green-500 rounded-full"
                      style={{ width: `${estimatePerformance.win_rate}%` }}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-slate-500 text-center py-8">No estimate data available</p>
            )}
          </CardContent>
        </Card>

        {/* Team Productivity */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Team Productivity
            </CardTitle>
          </CardHeader>
          <CardContent>
            {teamProductivity?.team_stats?.length > 0 ? (
              <div className="space-y-3">
                {teamProductivity.team_stats.slice(0, 5).map((member, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-medium">
                        {member.name.charAt(0)}
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{member.name}</p>
                        <p className="text-xs text-slate-500 capitalize">{member.role.replace('_', ' ')}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-indigo-600">{member.total_entries} entries</p>
                      <p className="text-xs text-slate-500">
                        <Clock className="w-3 h-3 inline mr-1" />
                        {member.total_break_minutes} min breaks
                      </p>
                    </div>
                  </div>
                ))}
                <div className="pt-3 border-t text-center text-sm text-slate-500">
                  {teamProductivity.total_team_members} team members • {teamProductivity.total_time_entries} total entries
                </div>
              </div>
            ) : (
              <p className="text-slate-500 text-center py-8">No timecard data available</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
